import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
  SelectChangeEvent
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../../../api/axios';

type Props = {
  open: boolean;
  userId?: number;
  userEmail?: string;
  onClose: () => void;
  onDeleted: () => void;
};

const CUSTOM_REASON_KEY = 'CUSTOM';

const reasonTranslations: Record<string, string> = {
  USER_REQUEST: 'system.user.delete.reason.userRequest',
  ORGANIZATION_REQUEST: 'system.user.delete.reason.organizationRequest',
  VIOLATION_OF_TERMS: 'system.user.delete.reason.violationOfTerms',
  SECURITY_BREACH: 'system.user.delete.reason.securityBreach',
  INACTIVE_ACCOUNT: 'system.user.delete.reason.inactiveAccount',
  CUSTOM: 'system.user.delete.reason.custom',
};

const UserDeleteModal = ({ open, userId, userEmail, onClose, onDeleted }: Props) => {
  const { t } = useTranslation();
  const [reasonOptions, setReasonOptions] = useState<string[]>([]);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchReasons = async () => {
      try {
        const response = await api.get('/user/delete-reasons');
        setReasonOptions(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Löschgründe:', error);
      }
    };

    fetchReasons();
  }, [open]);

  const handleReasonChange = (event: SelectChangeEvent) => {
    setSelectedReason(event.target.value);
    setCustomReason('');
  };

  const handleDelete = async () => {
    if (!userId) return;
    if (!selectedReason) return;

    const payload = {
      reason: selectedReason,
      customDeleteReason: selectedReason === CUSTOM_REASON_KEY ? customReason : null,
    };

    setLoading(true);
    try {
      await api.post(`/user/${userId}/delete-user-by-admin`, payload);

      onDeleted();
      onClose();
    } catch (error) {
      console.error('Fehler beim Löschen des Benutzers:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <div className="relative">
          <DialogTitle>{t('system.user.delete.title')}</DialogTitle>
          <IconButton
              aria-label="close"
              onClick={onClose}
              className="!absolute right-3 top-3 text-blue-600 hover:text-blue-800"
          >
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className="space-y-4">
          <div>{t('system.user.delete.text', { email: userEmail })}</div>

          <FormControl fullWidth>
            <InputLabel>{t('system.user.delete.reasonLabel')}</InputLabel>
            <Select
                value={selectedReason}
                onChange={handleReasonChange}
                label={t('system.user.delete.reasonLabel')}
            >
              {reasonOptions.map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    {t(reasonTranslations[reason] || reason)}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedReason === CUSTOM_REASON_KEY && (
              <TextField
                  label={t('system.user.delete.reason.customInput')}
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
              />
          )}
        </DialogContent>

        <DialogActions className="mr-4 mb-4">
          <Button onClick={onClose} className="text-gray-700 dark:text-gray-200">
            {t('system.user.delete.cancel')}
          </Button>
          <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={
                  loading ||
                  !userId ||
                  !selectedReason ||
                  (selectedReason === CUSTOM_REASON_KEY && !customReason.trim())
              }
          >
            {t('system.user.delete.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default UserDeleteModal;
